import { useEffect, useRef, useState, type CSSProperties } from "react";
import styled from "styled-components";

import { EyeIcon, EyeOffIcon, FilterIcon, TrashIcon } from "../../icons";
import { TooltipIconButton } from "../IconButton";
import { TextInput } from "../Input";
import { LabelChip } from "../LabelChip";
import { AnnotationPointMarkerGlyph } from "../MediaAnnotationStudio/AnnotationPointMarkerGlyph";
import { NotificationBadge } from "../NotificationBadge";
import { Spinner } from "../Spinner";
import { Text, defaultTextVariants } from "../Text";
import { TruncatedText } from "../TruncatedText";
import { controlFocusRing } from "../foundations/controlStyles";
import {
  CompositeListActionCell,
  CompositeListActionSlot,
  CompositeListPrimaryButton,
  CompositeListRow,
  CompositeListSurface,
} from "../foundations/compositeList";
import { useAnnotationTargetShortcuts } from "./useAnnotationTargetShortcuts";

export interface AnnotationTargetSlot {
  readonly id: string;
  readonly name?: string;
  readonly color: number;
  readonly occupied?: boolean;
  readonly dirty?: boolean;
  readonly pending?: boolean;
  readonly includeCount?: number;
  readonly excludeCount?: number;
}

export type AnnotationTargetStripPresentation = "list" | "palette";

export interface AnnotationTargetStripProps {
  targets: readonly AnnotationTargetSlot[];
  selectedTargetId: string | null;
  selectedOnly: boolean;
  visibleTargetIds: readonly string[];
  onSelectedOnlyChange: (selectedOnly: boolean) => void;
  onVisibleTargetIdsChange: (visibleTargetIds: readonly string[]) => void;
  onTargetChange: (targetId: string) => void;
  onClearTarget: (targetId: string) => void;
  onClearAllTargets: () => void;
  onTargetNameChange?: (targetId: string, name: string) => void;
  /** Detailed management list by default; palette stacks selection and visibility in one row. */
  presentation?: AnnotationTargetStripPresentation;
  /** Keeps selection and visibility interactive while omitting annotation mutation actions. */
  readOnly?: boolean;
  disabled?: boolean;
  activeLabel?: string;
  annotationLabel?: string;
  showSelectedOnlyLabel?: string;
  showAllTargetsLabel?: string;
  hideAllTargetsLabel?: string;
  showTargetLabel?: string;
  hideTargetLabel?: string;
  clearTargetLabel?: string;
  clearAllTargetsLabel?: string;
  renameTargetLabel?: string;
  unsavedChangesLabel?: string;
  positiveMarkerLabel?: string;
  negativeMarkerLabel?: string;
  pendingLabel?: string;
  occupiedLabel?: string;
  emptyLabel?: string;
  className?: string;
}

const Root = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.35rem;
`;

const Header = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const HeaderActions = styled.div`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 0.15rem;
`;

const PaletteHeader = styled.div`
  display: grid;
  min-width: 0;
  min-height: 1.75rem;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.35rem;
`;

const PaletteSelectedTarget = styled.div`
  display: flex;
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  align-items: center;
  gap: 0.4rem;
  padding: 1px 1px 1px 0.35rem;
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.control};

  & > [data-c6-tooltip-trigger] {
    flex: 0 0 auto;
  }

  & > [data-annotation-selected-target-counts] {
    display: flex;
    flex: 0 0 auto;
    font-size: ${({ theme }) => theme.typography.size.caption};
  }
`;

const PaletteSelectedTargetName = styled(TruncatedText)`
  color: ${({ theme }) => theme.color.text.default};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  /* Center the caption's visible glyphs against the swatch without moving the header controls. */
  transform: translateY(0.0625rem);
`;

const PaletteList = styled(CompositeListSurface)`
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(9, minmax(0, 1fr));
  background: ${({ theme }) => theme.color.bg.surfaceSunken};
`;

const PaletteTarget = styled.li`
  display: grid;
  min-width: 0;
  grid-template-rows: repeat(2, minmax(1.5rem, auto));

  & + & {
    border-left: 1px solid ${({ theme }) => theme.color.border.divider};
  }

  &[data-selected="true"] {
    background: ${({ theme }) => theme.color.bg.selected};
  }

  @media (forced-colors: active) {
    &[data-selected="true"] {
      outline: 1px solid Highlight;
      outline-offset: -1px;
    }
  }
`;

const PaletteSelectionCell = styled.div`
  min-width: 0;

  & > [data-c6-tooltip-trigger],
  & button {
    width: 100%;
  }

  & button {
    border-radius: 0;
  }

  & button:focus-visible {
    ${controlFocusRing("-2px")}
  }
`;

const PaletteVisibilityCell = styled(PaletteSelectionCell)``;

const PaletteTargetButton = styled(TooltipIconButton)`
  &[data-annotation-shortcut-focus]:focus-visible {
    outline: none;
  }
`;

// Selection and visibility are independent sibling actions in both list and palette layouts.
const TargetList = styled(CompositeListSurface)``;

const TargetRow = styled(CompositeListRow).attrs({ $size: "compact" as const })<{
  $clearable: boolean;
}>`
  grid-template-columns: ${({ $clearable }) =>
    $clearable ? "2rem minmax(0, 1fr) 2rem" : "2rem minmax(0, 1fr)"};
`;

const RowActionCell = styled(CompositeListActionCell).attrs({
  $size: "compact" as const,
})`
  justify-content: center;
`;

const VisibilityCell = styled(RowActionCell)``;

const ClearCell = styled(CompositeListActionSlot).attrs({
  $size: "compact" as const,
  $visible: false,
})``;

const TargetSelectButton = styled(CompositeListPrimaryButton).attrs({
  $size: "compact" as const,
})`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  /* The row's 1px bottom divider sits outside the grid content box. Let the primary content use
     that visual pixel so its top/bottom whitespace remains equal within the complete row. */
  padding: 0.25rem 0.55rem calc(0.25rem - 1px) 0.35rem;

  &[data-annotation-shortcut-focus]:focus-visible {
    outline: none;
  }
`;

const TargetNameSlot = styled.span`
  display: block;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const TargetSwatch = styled.span`
  width: 1.125rem;
  height: 1.125rem;
  box-sizing: border-box;
  flex: 0 0 auto;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: var(--c6-annotation-target-color);

  @media (forced-colors: active) {
    background: CanvasText;
  }
`;

const TargetSwatchBadge = styled(NotificationBadge)`
  flex: 0 0 auto;
`;

const PaletteTargetSwatchState = styled.span`
  display: inline-flex;
`;

const TargetShortcutKey = styled.kbd`
  display: inline-flex;
  width: 1.125rem;
  height: 1.125rem;
  box-sizing: border-box;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: ${({ theme }) => theme.color.bg.surfaceSunken};
  color: ${({ theme }) => theme.color.text.subtle};
  font-family: inherit;
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;

const TargetName = styled(Text)`
  display: block;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Pretendard's visible glyph mass sits slightly below its line box center. Keep the row box
     geometrically centered and apply the optical correction only to the label ink. */
  transform: translateY(-0.0625rem);
  white-space: nowrap;
`;

const TargetEditorCell = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 2rem;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.25rem 0 0.35rem;
`;

const TargetNameInput = styled(TextInput)`
  min-width: 0;
  flex: 1;
`;

const AnnotationCountBadge = styled(LabelChip)`
  flex: 0 0 auto;
  transform: translateY(-0.0625rem);
`;

const AnnotationCountContent = styled.span`
  /* A block flex avoids the inline baseline descent that otherwise lifts this 1rem marker group
     inside LabelChip's text wrapper. */
  display: flex;
  align-items: center;
  gap: 0.2rem;
  color: var(--c6-annotation-target-color);
  font-variant-numeric: tabular-nums;

  & > span {
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.color.text.default};
    line-height: 1;
  }
`;

function AnnotationCounts({
  includeCount,
  excludeCount,
}: {
  includeCount: number;
  excludeCount: number;
}) {
  return (
    <AnnotationCountContent>
      <AnnotationPointMarkerGlyph kind="include" variant="control" />
      <span data-annotation-count-value="">{includeCount}</span>
      <AnnotationPointMarkerGlyph kind="exclude" variant="control" />
      <span data-annotation-count-value="">{excludeCount}</span>
    </AnnotationCountContent>
  );
}

function colorCss(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

export function AnnotationTargetStrip({
  targets,
  selectedTargetId,
  selectedOnly,
  visibleTargetIds,
  onSelectedOnlyChange,
  onVisibleTargetIdsChange,
  onTargetChange,
  onClearTarget,
  onClearAllTargets,
  onTargetNameChange,
  presentation = "list",
  readOnly = false,
  disabled = false,
  activeLabel = "Annotation targets",
  annotationLabel = "Object slot",
  showSelectedOnlyLabel = "Show selected only",
  showAllTargetsLabel = "Show all",
  hideAllTargetsLabel = "Hide all",
  showTargetLabel = "Show",
  hideTargetLabel = "Hide",
  clearTargetLabel = "Clear annotation",
  clearAllTargetsLabel = "Clear all",
  renameTargetLabel = "Rename",
  unsavedChangesLabel = "Unsaved changes",
  positiveMarkerLabel = "Positive markers",
  negativeMarkerLabel = "Negative markers",
  pendingLabel = "Generating annotation",
  occupiedLabel = "Annotated",
  emptyLabel = "Empty",
  className,
}: AnnotationTargetStripProps) {
  const [editingTargetId, setEditingTargetId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const targetNameInputRef = useRef<HTMLInputElement>(null);
  const visibleTargetIdSet = new Set(visibleTargetIds);
  const allTargetsHidden = targets.every((target) => !visibleTargetIdSet.has(target.id));
  const selectedTargetExists = targets.some((target) => target.id === selectedTargetId);
  const hasOccupiedTargets = targets.some((target) => target.occupied);
  const targetShortcuts = useAnnotationTargetShortcuts({
    targets,
    selectedTargetId,
    selectedOnly,
    disabled,
    onSelectedOnlyChange,
    onVisibleTargetIdsChange,
    onTargetChange,
  });

  const changeTargetVisibility = (targetId: string, visible: boolean) => {
    if (selectedOnly) onSelectedOnlyChange(false);
    onVisibleTargetIdsChange(
      targets
        .filter((target) => (target.id === targetId ? visible : visibleTargetIdSet.has(target.id)))
        .map((target) => target.id),
    );
  };

  useEffect(() => {
    if (
      editingTargetId !== null &&
      (presentation === "palette" ||
        disabled ||
        readOnly ||
        !targets.some((target) => target.id === editingTargetId))
    ) {
      setEditingTargetId(null);
    }
  }, [disabled, editingTargetId, presentation, readOnly, targets]);

  useEffect(() => {
    if (editingTargetId === null) return;
    targetNameInputRef.current?.focus();
    targetNameInputRef.current?.select();
  }, [editingTargetId]);

  const beginTargetRename = (targetId: string, currentName: string) => {
    if (disabled || readOnly || !onTargetNameChange) return;
    if (targetId !== selectedTargetId) onTargetChange(targetId);
    setDraftName(currentName);
    setEditingTargetId(targetId);
  };

  const finishTargetRename = (targetId: string, currentName: string, commit: boolean) => {
    if (editingTargetId !== targetId) return;
    const nextName = draftName.trim();
    setEditingTargetId(null);
    if (commit && nextName && nextName !== currentName) {
      onTargetNameChange?.(targetId, nextName);
    }
  };

  const compact = presentation === "palette";
  const visibilityLabel = allTargetsHidden ? showAllTargetsLabel : hideAllTargetsLabel;
  const targetActions = (
    <HeaderActions
      data-annotation-target-actions=""
      onKeyDown={(event) => {
        if (event.key === " ") event.stopPropagation();
      }}
    >
      <TooltipIconButton
        size="xs"
        variant="surface"
        text={compact ? undefined : showSelectedOnlyLabel}
        tooltip={compact ? { content: showSelectedOnlyLabel, placement: "top" } : undefined}
        aria-pressed={selectedOnly}
        selected={selectedOnly}
        disabled={disabled || !selectedTargetExists}
        onClick={() => {
          onSelectedOnlyChange(!selectedOnly);
          onVisibleTargetIdsChange(
            selectedOnly
              ? targets.map((target) => target.id)
              : selectedTargetId === null
                ? []
                : [selectedTargetId],
          );
        }}
      >
        <FilterIcon />
      </TooltipIconButton>
      <TooltipIconButton
        size="xs"
        variant="surface"
        text={compact ? undefined : visibilityLabel}
        tooltip={compact ? { content: visibilityLabel, placement: "top" } : undefined}
        disabled={disabled || targets.length === 0 || (!hasOccupiedTargets && !allTargetsHidden)}
        onClick={() => {
          if (selectedOnly) onSelectedOnlyChange(false);
          onVisibleTargetIdsChange(allTargetsHidden ? targets.map((target) => target.id) : []);
        }}
      >
        {allTargetsHidden ? <EyeIcon /> : <EyeOffIcon />}
      </TooltipIconButton>
      {readOnly ? null : (
        <TooltipIconButton
          size="xs"
          variant="surface"
          tone="danger"
          text={compact ? undefined : clearAllTargetsLabel}
          tooltip={compact ? { content: clearAllTargetsLabel, placement: "top" } : undefined}
          disabled={disabled || !hasOccupiedTargets}
          onClick={onClearAllTargets}
        >
          <TrashIcon />
        </TooltipIconButton>
      )}
    </HeaderActions>
  );

  if (presentation === "palette") {
    const selectedTargetIndex = targets.findIndex((target) => target.id === selectedTargetId);
    const selectedTarget = selectedTargetIndex < 0 ? undefined : targets[selectedTargetIndex];
    const selectedTargetOccupied = selectedTarget?.occupied ?? false;
    const selectedIncludeCount = Math.max(0, Math.floor(selectedTarget?.includeCount ?? 0));
    const selectedExcludeCount = Math.max(0, Math.floor(selectedTarget?.excludeCount ?? 0));
    const selectedTargetName = selectedTarget?.name?.trim();
    const selectedTargetLabel = selectedTarget
      ? selectedTargetName || `${annotationLabel} ${selectedTargetIndex + 1}`
      : activeLabel;

    return (
      <Root
        className={className}
        data-annotation-target-strip=""
        data-annotation-target-presentation="palette"
        aria-keyshortcuts={targetShortcuts.ariaKeyShortcuts || undefined}
        onKeyDown={targetShortcuts.onKeyDown}
      >
        <PaletteHeader data-annotation-palette-header="">
          <PaletteSelectedTarget
            data-annotation-selected-target=""
            role="group"
            aria-label={selectedTargetLabel}
            style={
              selectedTarget
                ? ({
                    "--c6-annotation-target-color": colorCss(selectedTarget.color),
                  } as CSSProperties)
                : undefined
            }
          >
            {selectedTarget ? <TargetSwatch aria-hidden="true" /> : null}
            <PaletteSelectedTargetName
              data-annotation-selected-target-name=""
              fullText={selectedTargetLabel}
            >
              {selectedTargetLabel}
            </PaletteSelectedTargetName>
            {selectedTarget ? (
              <span
                aria-label={`${positiveMarkerLabel} ${selectedIncludeCount} · ${negativeMarkerLabel} ${selectedExcludeCount}`}
                data-annotation-selected-target-counts=""
              >
                <AnnotationCounts
                  includeCount={selectedIncludeCount}
                  excludeCount={selectedExcludeCount}
                />
              </span>
            ) : null}
            {selectedTarget?.pending ? <Spinner size="sm" label={pendingLabel} /> : null}
            {readOnly ? null : (
              <TooltipIconButton
                data-annotation-selected-target-clear=""
                size="xs"
                variant="ghost"
                tone="danger"
                tooltip={{
                  content: `${selectedTargetLabel} · ${clearTargetLabel}`,
                  placement: "top",
                }}
                disabledTooltip={
                  !selectedTargetOccupied ? `${selectedTargetLabel} · ${emptyLabel}` : undefined
                }
                disabled={disabled || !selectedTargetOccupied}
                onClick={() => {
                  if (selectedTarget) onClearTarget(selectedTarget.id);
                }}
                onKeyDown={(event) => {
                  if (event.key === " ") event.stopPropagation();
                }}
              >
                <TrashIcon />
              </TooltipIconButton>
            )}
          </PaletteSelectedTarget>
          {targetActions}
        </PaletteHeader>
        <PaletteList aria-label={activeLabel} data-annotation-target-palette="">
          {targets.map((target, index) => {
            const selected = target.id === selectedTargetId;
            const visible = visibleTargetIdSet.has(target.id);
            const occupied = target.occupied ?? false;
            const showDirty = (target.dirty ?? false) && occupied;
            const includeCount = Math.max(0, Math.floor(target.includeCount ?? 0));
            const excludeCount = Math.max(0, Math.floor(target.excludeCount ?? 0));
            const annotationCountLabel = `${positiveMarkerLabel} ${includeCount} · ${negativeMarkerLabel} ${excludeCount}`;
            const slotLabel = target.name?.trim() || `${annotationLabel} ${index + 1}`;
            const occupancyLabel = occupied ? occupiedLabel : emptyLabel;
            const targetSummary = `${slotLabel} · ${occupancyLabel}${
              showDirty ? ` · ${unsavedChangesLabel}` : ""
            }${occupied ? ` · ${annotationCountLabel}` : ""}`;
            const visibilityActionLabel = `${slotLabel} · ${
              visible ? hideTargetLabel : showTargetLabel
            }`;
            const slotIndex = index < 9 ? String(index + 1) : undefined;

            return (
              <PaletteTarget
                key={target.id}
                data-annotation-slot={index + 1}
                data-dirty={showDirty}
                data-occupied={occupied}
                data-selected={selected}
                data-visible={visible}
              >
                <PaletteSelectionCell>
                  <PaletteTargetButton
                    data-annotation-target-select=""
                    size="xs"
                    variant="ghost"
                    aria-pressed={selected}
                    aria-keyshortcuts={disabled ? undefined : slotIndex}
                    tooltip={{ content: targetSummary, placement: "top" }}
                    disabled={disabled}
                    style={
                      {
                        "--c6-annotation-target-color": colorCss(target.color),
                      } as CSSProperties
                    }
                    onClick={() => onTargetChange(target.id)}
                    onPointerDown={(event) => {
                      event.currentTarget.removeAttribute("data-annotation-shortcut-focus");
                    }}
                    onBlur={(event) => {
                      event.currentTarget.removeAttribute("data-annotation-shortcut-focus");
                    }}
                    onKeyDown={(event) => {
                      if (!/^[0-9]$/.test(event.key)) {
                        event.currentTarget.removeAttribute("data-annotation-shortcut-focus");
                      }
                    }}
                  >
                    <PaletteTargetSwatchState
                      data-annotation-target-dirty-indicator={showDirty ? "" : undefined}
                    >
                      <TargetSwatchBadge
                        visible={showDirty}
                        tone="warning"
                        size="0.45rem"
                        offset="-0.12rem"
                      >
                        <TargetSwatch aria-hidden="true" />
                      </TargetSwatchBadge>
                    </PaletteTargetSwatchState>
                  </PaletteTargetButton>
                </PaletteSelectionCell>
                <PaletteVisibilityCell>
                  <TooltipIconButton
                    size="xs"
                    variant="ghost"
                    tooltip={{ content: visibilityActionLabel, placement: "top" }}
                    aria-pressed={visible}
                    disabled={disabled}
                    onClick={() => changeTargetVisibility(target.id, !visible)}
                  >
                    {visible ? <EyeIcon /> : <EyeOffIcon />}
                  </TooltipIconButton>
                </PaletteVisibilityCell>
              </PaletteTarget>
            );
          })}
        </PaletteList>
      </Root>
    );
  }

  return (
    <Root
      className={className}
      data-annotation-target-strip=""
      data-annotation-target-presentation="list"
      aria-keyshortcuts={targetShortcuts.ariaKeyShortcuts || undefined}
      onKeyDown={targetShortcuts.onKeyDown}
    >
      <Header>
        <Text variant={defaultTextVariants["caption-bold"]}>{activeLabel}</Text>
        {targetActions}
      </Header>
      <TargetList aria-label={activeLabel}>
        {targets.map((target, index) => {
          const selected = target.id === selectedTargetId;
          const visible = visibleTargetIdSet.has(target.id);
          const occupied = target.occupied ?? false;
          const dirty = target.dirty ?? false;
          const showDirty = dirty && occupied;
          const pending = target.pending ?? false;
          const includeCount = Math.max(0, Math.floor(target.includeCount ?? 0));
          const excludeCount = Math.max(0, Math.floor(target.excludeCount ?? 0));
          const annotationCountLabel = `${positiveMarkerLabel} ${includeCount} · ${negativeMarkerLabel} ${excludeCount}`;
          const renameEnabled = !disabled && !readOnly && onTargetNameChange !== undefined;
          const defaultSlotLabel = `${annotationLabel} ${index + 1}`;
          const customSlotName = target.name?.trim();
          const slotLabel = customSlotName || defaultSlotLabel;
          const displaySlotName = slotLabel;
          const slotIndex = index < 9 ? String(index + 1) : null;
          const targetKeyShortcuts =
            !disabled && slotIndex
              ? renameEnabled
                ? `${slotIndex} F2`
                : slotIndex
              : renameEnabled
                ? "F2"
                : undefined;
          const occupancyLabel = occupied ? occupiedLabel : emptyLabel;
          const visibilityActionLabel = `${slotLabel} · ${
            visible ? hideTargetLabel : showTargetLabel
          }`;

          return (
            <TargetRow
              key={target.id}
              $selected={selected}
              $clearable={occupied && !readOnly}
              $disabled={disabled}
              data-annotation-slot={index + 1}
              data-dirty={showDirty}
              data-occupied={occupied}
              data-selected={selected}
              data-visible={visible}
            >
              <VisibilityCell>
                <TooltipIconButton
                  size="xs"
                  variant="ghost"
                  tooltip={{
                    content: visible ? hideTargetLabel : showTargetLabel,
                    placement: "left",
                  }}
                  aria-label={visibilityActionLabel}
                  aria-pressed={visible}
                  disabled={disabled}
                  onClick={() => changeTargetVisibility(target.id, !visible)}
                >
                  {visible ? <EyeIcon /> : <EyeOffIcon />}
                </TooltipIconButton>
              </VisibilityCell>
              {editingTargetId === target.id ? (
                <TargetEditorCell
                  style={
                    { "--c6-annotation-target-color": colorCss(target.color) } as CSSProperties
                  }
                >
                  <TargetSwatchBadge
                    visible={showDirty}
                    tone="warning"
                    size="0.45rem"
                    offset="-0.12rem"
                  >
                    <TargetSwatch aria-hidden="true" />
                  </TargetSwatchBadge>
                  {slotIndex ? (
                    <TargetShortcutKey aria-hidden="true" data-annotation-slot-index="">
                      {slotIndex}
                    </TargetShortcutKey>
                  ) : null}
                  <TargetNameInput
                    ref={targetNameInputRef}
                    inputSize="sm"
                    value={draftName}
                    maxLength={64}
                    aria-label={`${slotLabel} · ${renameTargetLabel}`}
                    onChange={(event) => setDraftName(event.currentTarget.value)}
                    onBlur={() => finishTargetRename(target.id, slotLabel, true)}
                    onEnterPress={(event) => {
                      event.preventDefault();
                      finishTargetRename(target.id, slotLabel, true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Escape") return;
                      event.preventDefault();
                      event.stopPropagation();
                      finishTargetRename(target.id, slotLabel, false);
                    }}
                  />
                  {pending ? <Spinner size="sm" label={pendingLabel} /> : null}
                  {occupied ? (
                    <AnnotationCountBadge
                      color={target.color}
                      size="xs"
                      variant="filled"
                      showIcon={false}
                      aria-label={annotationCountLabel}
                      data-annotation-occupied-indicator=""
                    >
                      <AnnotationCounts includeCount={includeCount} excludeCount={excludeCount} />
                    </AnnotationCountBadge>
                  ) : null}
                </TargetEditorCell>
              ) : (
                <TargetSelectButton
                  type="button"
                  data-studio-list-primary=""
                  data-annotation-target-select=""
                  aria-pressed={selected}
                  aria-keyshortcuts={targetKeyShortcuts}
                  aria-label={`${slotLabel} · ${occupancyLabel}${
                    showDirty ? ` · ${unsavedChangesLabel}` : ""
                  }${occupied ? ` · ${annotationCountLabel}` : ""}`}
                  disabled={disabled}
                  style={
                    {
                      "--c6-annotation-target-color": colorCss(target.color),
                    } as CSSProperties
                  }
                  onClick={() => onTargetChange(target.id)}
                  onPointerDown={(event) => {
                    event.currentTarget.removeAttribute("data-annotation-shortcut-focus");
                  }}
                  onBlur={(event) => {
                    event.currentTarget.removeAttribute("data-annotation-shortcut-focus");
                  }}
                  onKeyDown={(event) => {
                    const bareNumericShortcut =
                      /^[0-9]$/.test(event.key) &&
                      !event.altKey &&
                      !event.ctrlKey &&
                      !event.metaKey &&
                      !event.shiftKey;
                    if (!bareNumericShortcut) {
                      event.currentTarget.removeAttribute("data-annotation-shortcut-focus");
                    }
                    if (event.key !== "F2" || !renameEnabled) return;
                    event.preventDefault();
                    beginTargetRename(target.id, slotLabel);
                  }}
                >
                  <TargetSwatchBadge
                    visible={showDirty}
                    tone="warning"
                    size="0.45rem"
                    offset="-0.12rem"
                  >
                    <TargetSwatch aria-hidden="true" />
                  </TargetSwatchBadge>
                  {slotIndex ? (
                    <TargetShortcutKey aria-hidden="true" data-annotation-slot-index="">
                      {slotIndex}
                    </TargetShortcutKey>
                  ) : null}
                  <TargetNameSlot>
                    <TargetName
                      data-annotation-target-name=""
                      variant={
                        selected
                          ? defaultTextVariants["caption-bold"]
                          : defaultTextVariants["caption-normal"]
                      }
                      onDoubleClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        beginTargetRename(target.id, slotLabel);
                      }}
                    >
                      {displaySlotName}
                    </TargetName>
                  </TargetNameSlot>
                  {pending ? <Spinner size="sm" label={pendingLabel} /> : null}
                  {occupied ? (
                    <AnnotationCountBadge
                      color={target.color}
                      size="xs"
                      variant="filled"
                      showIcon={false}
                      aria-label={annotationCountLabel}
                      data-annotation-occupied-indicator=""
                    >
                      <AnnotationCounts includeCount={includeCount} excludeCount={excludeCount} />
                    </AnnotationCountBadge>
                  ) : null}
                </TargetSelectButton>
              )}
              {occupied && !readOnly ? (
                <ClearCell data-studio-list-action-slot="" data-visible="false">
                  <TooltipIconButton
                    data-studio-list-danger=""
                    size="xs"
                    variant="ghost"
                    tone="danger"
                    tooltip={{
                      content: `${slotLabel} · ${clearTargetLabel}`,
                      placement: "left",
                    }}
                    disabled={disabled}
                    onClick={() => onClearTarget(target.id)}
                  >
                    <TrashIcon />
                  </TooltipIconButton>
                </ClearCell>
              ) : null}
            </TargetRow>
          );
        })}
      </TargetList>
    </Root>
  );
}
